// src/Components/ImportMarkers/ImportKml.js

import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';

// Navigation Icons
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';

// Import Custom Icons
import {
  MenuAddNew,
  MenuAddNewGrey,
  MenuSaveGrey,
  MenuDelete,
  MenuDeleteGrey,
  MenuIconCancel,
} from '../../assets/icons/CustomIcons';

import {
  MenuImport,
  MenuImportGrey,
  MenuUpload,
  MenuUploadGrey,
} from '../../assets/icons/CustomIconsAdd2';

import {
  MenuReinstate,
  MenuReinstateGrey,
  MenuMoveFile,
  MenuMoveFileGrey,
} from '../../assets/icons/CustomIconsAdd4';

import AdminBg from '../../assets/images/AdminBackground.png';

// Import Sub-Components
import KmlDataView from './KmlDataView';
import KmlAssignData from './KmlAssignData';
import KmlHistoryView from './KmlHistoryView';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

// Import Hook
import { useKmlManage } from '../../Hooks/useKmlManage';

// --- STYLING CONSTANTS ---
const ICON_SIZE = 64;

// PAGE 1 (DATA_VIEW) UPLOAD ICON SETTINGS
const PAGE1_UPLOAD_SCALE = 4.9;
const PAGE1_UPLOAD_ICON_X = 25;
const PAGE1_UPLOAD_ICON_Y = 38;
const PAGE1_UPLOAD_TEXT_SIZE = '18px';
const PAGE1_UPLOAD_TEXT_Y = 108;

// PAGE 2 (SETTINGS_VIEW) IMPORT ICON SETTINGS
const PAGE2_IMPORT_SCALE = 7.5;
const PAGE2_IMPORT_ICON_X = 25;
const PAGE2_IMPORT_ICON_Y = 37;
const PAGE2_IMPORT_TEXT_SIZE = '18px';
const PAGE2_IMPORT_TEXT_Y = 108;

const STEPS = ['DATA_VIEW', 'SETTINGS_VIEW', 'HISTORY_VIEW'];

export default function ImportKml({ setIsDirty, setHasKmlQueue, excelHasData }) {
  const {
    state: {
      fileInputRef,
      viewMode,
      parsedData,
      currentFilename,
      originalFilename,
      selectedImportIds,
      categories,
      regions,
      globalCategory,
      customRows,
      markerIcons,
      selectedCustomIds,
      rowMappings,
      rowSubMappings,
      rowStaticValues,
      defaultPublicFromDate,
      makeUppercase,
      isProcessing,
      importHistory,
      isLoadingHistory,
      selectedHistoryIds,
    },
    flags: { hasData, canSave, canDeleteData, canUndoHistory },
    alerts: {
      alertState,
      showDeleteConfirm,
      showDeleteConfirm2,
      showMoveConfirm,
      showCloseConfirm,
      restrictedAlert,
      showBatchFilenameDialog,
      confirmDialog,
    },
    actions: {
      setSelectedImportIds,
      setGlobalCategory,
      setSelectedHistoryIds,
      setAlertState,
      setShowDeleteConfirm,
      setShowDeleteConfirm2,
      setShowMoveConfirm,
      setShowCloseConfirm,
      handleAddCustomRow,
      handleCustomRowNameChange,
      setSelectedCustomIds,
      setRowMappings,
      setRowSubMappings,
      setRowStaticValues,
      handleNavFirst,
      handleNavBack,
      handleNavForward,
      handleNavLast,
      handleImportClick,
      handleFileChange,
      handleCloseClick,
      confirmClearData,
      handleSave,
      confirmImportBatch,
      executeDeleteBatches,
      handleMoveClick,
      executeMoveBatches,
      handleDeleteClick,
      handleAcknowledgeRestricted,
      handleReinstateClick,
      setShowBatchFilenameDialog,
      setConfirmDialog,
    },
  } = useKmlManage({ setIsDirty });

  const isExcelLoaded = Boolean(excelHasData);

  const [tempBatchFilename, setTempBatchFilename] = useState('');

  useEffect(() => {
    if (showBatchFilenameDialog) {
      setTempBatchFilename(currentFilename || originalFilename || '');
    }
  }, [showBatchFilenameDialog, currentFilename, originalFilename]);

  useEffect(() => {
    if (setHasKmlQueue) {
      setHasKmlQueue(hasData);
    }
  }, [hasData, setHasKmlQueue]);

  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  const selectedHistoryBatches =
    importHistory?.filter((b) => selectedHistoryIds?.includes(b.id)) || [];
  const totalMarkersToDelete = selectedHistoryBatches.reduce(
    (acc, batch) => acc + (batch.markerCount || 0),
    0,
  );
  const batchCount = selectedHistoryIds?.length || 0;
  const batchText = batchCount === 1 ? 'batch' : 'batches';

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
      {/* LOADING OVERLAY */}
      {isProcessing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 100002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
          }}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#00B0FF' }} />
        </Box>
      )}

      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept='.kml,.kmz'
        onChange={handleFileChange}
      />

      {/* --- TOP TOOLBAR --- */}
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          paddingLeft: '5px',
          paddingRight: '5px',
        }}>
        {/* ADD NEW Button activates only on SETTINGS_VIEW */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            disabled={viewMode !== 'SETTINGS_VIEW' || isProcessing}
            onClick={handleAddCustomRow}>
            {viewMode === 'SETTINGS_VIEW' && !isProcessing ? (
              <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* UPLOAD KML FILE */}
        <Box sx={toolBlockStyle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton
              sx={{ p: 0, transform: 'translateY(-5px)' }}
              onClick={handleImportClick}
              disabled={
                isProcessing || hasData || isExcelLoaded || viewMode !== 'DATA_VIEW'
              }
              title={
                isExcelLoaded ? 'Clear Batch Import data first' : 'Upload KML/KMZ File'
              }>
              {viewMode === 'DATA_VIEW' && !isProcessing && !hasData && !isExcelLoaded ? (
                <MenuUpload
                  sx={{ fontSize: ICON_SIZE }}
                  iconScale={PAGE1_UPLOAD_SCALE}
                  iconX={PAGE1_UPLOAD_ICON_X}
                  iconY={PAGE1_UPLOAD_ICON_Y}
                  textSize={PAGE1_UPLOAD_TEXT_SIZE}
                  textY={PAGE1_UPLOAD_TEXT_Y}
                  label='Upload'
                />
              ) : (
                <MenuUploadGrey
                  sx={{ fontSize: ICON_SIZE }}
                  iconScale={PAGE1_UPLOAD_SCALE}
                  iconX={PAGE1_UPLOAD_ICON_X}
                  iconY={PAGE1_UPLOAD_ICON_Y}
                  textSize={PAGE1_UPLOAD_TEXT_SIZE}
                  textY={PAGE1_UPLOAD_TEXT_Y}
                  label='Upload'
                />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* IMPORT DATA TO MAP */}
        <Box sx={toolBlockStyle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton
              sx={{ p: 0, transform: 'translateY(-5px)' }}
              onClick={handleSave}
              disabled={!canSave}
              title='Import Data to Map'>
              {canSave ? (
                <MenuImport
                  sx={{ fontSize: ICON_SIZE }}
                  iconScale={PAGE2_IMPORT_SCALE}
                  iconX={PAGE2_IMPORT_ICON_X}
                  iconY={PAGE2_IMPORT_ICON_Y}
                  textSize={PAGE2_IMPORT_TEXT_SIZE}
                  textY={PAGE2_IMPORT_TEXT_Y}
                  label='Import'
                />
              ) : (
                <MenuImportGrey
                  sx={{ fontSize: ICON_SIZE }}
                  iconScale={PAGE2_IMPORT_SCALE}
                  iconX={PAGE2_IMPORT_ICON_X}
                  iconY={PAGE2_IMPORT_ICON_Y}
                  textSize={PAGE2_IMPORT_TEXT_SIZE}
                  textY={PAGE2_IMPORT_TEXT_Y}
                  label='Import'
                />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* SAVE (Now Inactive) */}
        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        {/* DELETE (Clear Staging OR Reverse Import) */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            disabled={
              (!canDeleteData && viewMode !== 'DATA_VIEW') ||
              isProcessing ||
              (viewMode === 'DATA_VIEW' && !hasData)
            }
            onClick={handleDeleteClick}>
            {canDeleteData || (viewMode === 'DATA_VIEW' && hasData) ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* CANCEL/CLOSE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleCloseClick}
            disabled={isProcessing}>
            <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>
      </Box>

      {/* --- SECONDARY HEADER FOR HISTORY VIEW --- */}
      {viewMode === 'HISTORY_VIEW' && (
        <Box
          sx={{
            height: '55px',
            minHeight: '55px',
            maxHeight: '55px',
            overflow: 'hidden',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f9f9f9',
            borderBottom: '1px solid #ccc',
            flexShrink: 0,
            zIndex: 49,
            px: 2,
            py: 0.5,
            boxSizing: 'border-box',
          }}>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              pr: 2,
              gap: 0.5,
            }}></Box>

          <Divider orientation='vertical' flexItem sx={{ my: 1, borderColor: '#ccc' }} />

          <Box
            sx={{
              width: '110px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              ml: 1,
            }}>
            <IconButton
              sx={{ p: 0, transform: 'translateY(5px)' }}
              disabled={!canUndoHistory || selectedHistoryIds?.length === 0}
              onClick={handleReinstateClick}
              title='Reinstate Selected Batches'>
              {canUndoHistory && selectedHistoryIds?.length > 0 ? (
                <MenuReinstate sx={{ fontSize: ICON_SIZE * 1.5 }} />
              ) : (
                <MenuReinstateGrey sx={{ fontSize: ICON_SIZE * 1.5 }} />
              )}
            </IconButton>
          </Box>

          <Divider orientation='vertical' flexItem sx={{ my: 1, borderColor: '#ccc' }} />

          <Box
            sx={{
              width: '110px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              ml: 1,
            }}>
            <IconButton
              sx={{ p: 0, transform: 'translateY(-5px)' }}
              disabled={!canUndoHistory}
              onClick={handleMoveClick}
              title='Move to Batch Import'>
              {canUndoHistory ? (
                <MenuMoveFile sx={{ fontSize: ICON_SIZE * 1.5 }} />
              ) : (
                <MenuMoveFileGrey sx={{ fontSize: ICON_SIZE * 1.5 }} />
              )}
            </IconButton>
          </Box>
        </Box>
      )}

      {/* --- DYNAMIC CONTENT AREA --- */}
      <Box
        sx={{
          flexGrow: 1,
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          mt: viewMode === 'HISTORY_VIEW' ? 0 : 2,
        }}>
        {viewMode === 'DATA_VIEW' && (
          <KmlDataView
            parsedData={parsedData}
            selectedImportIds={selectedImportIds}
            setSelectedImportIds={setSelectedImportIds}
            excelHasData={isExcelLoaded}
            makeUppercase={makeUppercase}
          />
        )}

        {viewMode === 'SETTINGS_VIEW' && (
          <KmlAssignData
            parsedData={parsedData}
            selectedImportIds={selectedImportIds}
            categories={categories}
            regions={regions}
            globalCategory={globalCategory}
            setGlobalCategory={setGlobalCategory}
            customRows={customRows}
            handleCustomRowNameChange={handleCustomRowNameChange}
            markerIcons={markerIcons}
            selectedCustomIds={selectedCustomIds}
            setSelectedCustomIds={setSelectedCustomIds}
            rowMappings={rowMappings}
            setRowMappings={setRowMappings}
            rowSubMappings={rowSubMappings}
            setRowSubMappings={setRowSubMappings}
            rowStaticValues={rowStaticValues}
            setRowStaticValues={setRowStaticValues}
            defaultPublicFromDate={defaultPublicFromDate}
            makeUppercase={makeUppercase}
          />
        )}

        {viewMode === 'HISTORY_VIEW' && (
          <KmlHistoryView
            importHistory={importHistory}
            isLoadingHistory={isLoadingHistory}
            selectedHistoryIds={selectedHistoryIds}
            setSelectedHistoryIds={setSelectedHistoryIds}
          />
        )}
      </Box>

      {/* --- FOOTER PAGINATION --- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 0.5,
          minHeight: '40px',
          borderTop: '1px solid black',
          backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
          position: 'relative',
        }}>
        <Box sx={{ minWidth: '150px', display: 'flex', alignItems: 'center' }}>
          {(viewMode === 'DATA_VIEW' || viewMode === 'SETTINGS_VIEW') && hasData && (
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#333' }}>
              Total Data Points: {parsedData.length}
            </Typography>
          )}
        </Box>

        {/* PAGE INDICATOR DOTS */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            gap: 1,
          }}>
          {STEPS.map((step) =>
            viewMode === step ? (
              <CircleIcon key={step} sx={{ fontSize: '14px', color: '#2e7d32' }} />
            ) : (
              <CircleOutlinedIcon key={step} sx={{ fontSize: '14px', color: '#666' }} />
            ),
          )}
        </Box>

        {/* NAVIGATION ARROWS */}
        <Box
          sx={{ display: 'flex', gap: 1, minWidth: '150px', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={handleNavFirst}
            disabled={viewMode === 'DATA_VIEW' || isProcessing}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <FirstPageIcon
              sx={{ color: viewMode === 'DATA_VIEW' ? 'grey' : 'black', fontSize: 22 }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavBack}
            disabled={viewMode === 'DATA_VIEW' || isProcessing}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowBackIosNewIcon
              sx={{ color: viewMode === 'DATA_VIEW' ? 'grey' : 'black', fontSize: 22 }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavForward}
            disabled={viewMode === 'HISTORY_VIEW' || isProcessing}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowForwardIosIcon
              sx={{ color: viewMode === 'HISTORY_VIEW' ? 'grey' : 'black', fontSize: 22 }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavLast}
            disabled={viewMode === 'HISTORY_VIEW' || isProcessing}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <LastPageIcon
              sx={{ color: viewMode === 'HISTORY_VIEW' ? 'grey' : 'black', fontSize: 22 }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* --- DIALOGS --- */}

      <ConfirmDialog
        open={confirmDialog?.open || false}
        onClose={() => setConfirmDialog({ ...(confirmDialog || {}), open: false })}
        onConfirm={() => {
          if (confirmDialog?.onConfirm) {
            confirmDialog.onConfirm();
          }
        }}
        message={confirmDialog?.message || ''}
        severity={confirmDialog?.severity || 'info'}
        confirmText={confirmDialog?.confirmText || 'Yes'}
        cancelText={confirmDialog?.cancelText || 'No'}
        hideCancel={confirmDialog?.hideCancel || false}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
      />

      <ConfirmDialog
        open={showBatchFilenameDialog || false}
        onClose={() => setShowBatchFilenameDialog(false)}
        onConfirm={() => {
          if (typeof confirmImportBatch === 'function') {
            confirmImportBatch(tempBatchFilename);
          }
        }}
        message={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography>
              Please confirm or edit the Batch Filename for this import:
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size='small'
              variant='outlined'
              label='Batch Filename'
              value={tempBatchFilename}
              onChange={(e) => setTempBatchFilename(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (typeof confirmImportBatch === 'function') {
                    confirmImportBatch(e.target.value);
                  }
                }
              }}
            />
          </Box>
        }
        severity='info'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={restrictedAlert.open}
        onClose={handleAcknowledgeRestricted}
        onConfirm={handleAcknowledgeRestricted}
        message='The images are not available for download due to Google restrictions.'
        severity='warning'
        confirmText='OK'
        hideCancel={true}
        disableBackdropClick={true}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={confirmClearData}
        message='Cancel will discard your data and session. Are you sure?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          setShowDeleteConfirm2(true);
        }}
        message={`This will delete ${totalMarkersToDelete} markers in the ${batchText} you have selected. Do you want to proceed?`}
        severity='warning'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={showDeleteConfirm2}
        onClose={() => setShowDeleteConfirm2(false)}
        onConfirm={() => {
          setShowDeleteConfirm2(false);
          if (typeof executeDeleteBatches === 'function') {
            executeDeleteBatches();
          }
        }}
        message={`Are you absolutely sure you want to permanently delete these ${totalMarkersToDelete} markers? This action cannot be undone.`}
        severity='error'
        confirmText='Yes, Delete'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={showMoveConfirm}
        onClose={() => setShowMoveConfirm(false)}
        onConfirm={() => {
          setShowMoveConfirm(false);
          if (typeof executeMoveBatches === 'function') {
            executeMoveBatches();
          }
        }}
        message='Are you sure you want to move the selected batches to the Batch Import module for further editing?'
        severity='info'
        confirmText='Move Batches'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alertState.open}
        onClose={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        severity={alertState.severity}
        autoClose={
          alertState.severity === 'success' || alertState.severity === 'info'
            ? 3000
            : null
        }
        confirmText='OK'
        hideCancel={true}
        disableBackdropClick={true}
        onConfirm={() => setAlertState({ ...alertState, open: false })}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </Box>
  );
}
